import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Download, FileText, AlertCircle, Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";

interface SharedPitchDeck {
  id: string;
  file_key: string;
  share_token: string;
  created_at: string;
  file_size: number | null;
}

export default function Share() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pitchDeck, setPitchDeck] = useState<SharedPitchDeck | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSharedDeck = async () => {
      if (!shareToken) {
        navigate("/");
        return;
      }

      try {
        // Fetch pitch deck by share token via SECURITY DEFINER RPC
        // (table is no longer publicly readable by share_token IS NOT NULL)
        const { data: deckRows, error: deckError } = await supabase.rpc(
          "get_pitch_deck_by_share_token",
          { _token: shareToken }
        );

        if (deckError) {
          console.error("Error fetching deck:", deckError);
          setError("Failed to load pitch deck");
          setLoading(false);
          return;
        }

        const deck = Array.isArray(deckRows) ? deckRows[0] : deckRows;

        if (!deck) {
          setError("This share link is invalid or has expired");
          setLoading(false);
          return;
        }

        setPitchDeck(deck as SharedPitchDeck);

        // Get signed URL for the file
        const { data: signedUrlData, error: urlError } = await supabase.storage
          .from("pitch-decks")
          .createSignedUrl(deck.file_key, 3600); // 1 hour

        if (urlError) {
          console.error("Error creating signed URL:", urlError);
          setError("Failed to access the pitch deck file");
          setLoading(false);
          return;
        }

        if (signedUrlData) {
          setFileUrl(signedUrlData.signedUrl);
        }
      } catch (err) {
        console.error("Error loading shared deck:", err);
        setError("An unexpected error occurred");
      } finally {
        setLoading(false);
      }
    };

    loadSharedDeck();
  }, [shareToken, navigate]);

  const handleDownload = () => {
    if (fileUrl) {
      window.open(fileUrl, "_blank");
      toast({
        title: "Download started",
        description: "Your pitch deck is downloading",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-2xl space-y-6">
          <Skeleton className="h-12 w-64 mx-auto" />
          <Skeleton className="h-[400px] w-full" />
          <Skeleton className="h-10 w-40 mx-auto" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Link Not Available</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button variant="outline" onClick={() => navigate("/")}>
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!pitchDeck || !fileUrl) {
    return null;
  }

  const isPdf = pitchDeck.file_key.toLowerCase().endsWith(".pdf");

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="font-semibold">Shared Pitch Deck</h1>
                <p className="text-sm text-muted-foreground">
                  Created {new Date(pitchDeck.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <Button onClick={handleDownload}>
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {isPdf ? (
            <div className="rounded-lg border bg-card overflow-hidden shadow-lg">
              <iframe
                src={`${fileUrl}#toolbar=0`}
                className="w-full h-[80vh]"
                title="Pitch Deck Preview"
              />
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                <h2 className="text-lg font-semibold mb-2">
                  Preview Not Available
                </h2>
                <p className="text-muted-foreground mb-6">
                  This file type cannot be previewed in the browser.
                  Download it to view the full pitch deck.
                </p>
                <Button onClick={handleDownload}>
                  <Download className="mr-2 h-4 w-4" />
                  Download to View
                </Button>
              </CardContent>
            </Card>
          )}

          {/* File info */}
          <div className="mt-4 text-center text-sm text-muted-foreground">
            {pitchDeck.file_size && (
              <span>File size: {Math.round(pitchDeck.file_size / 1024)} KB</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
